import { bauBaum, istFormel, zerlege } from "./formelzerleger";
import { FEHLER, LEER, fehler, istFehler, werteAus } from "./formelrechner";
import { deuteEingabe } from "./zahlformat";
import { zellschluessel } from "./zellbezug";

/*
  Das ganze Blatt durchrechnen.

  ---------------------------------------------------------------
  Warum das Yjs nie zu sehen bekommt
  ---------------------------------------------------------------

  Herein kommt eine schlichte Momentaufnahme aus `alsGitter(doc)` — Listen
  und eine Map, mehr nicht. Damit ist der riskanteste Teil des ganzen
  Bereichs ohne Netz, ohne Oberfläche und ohne Yjs prüfbar, und die
  Prüfungen laufen in Millisekunden statt in Sekunden.

  ---------------------------------------------------------------
  Warum Tiefensuche mit Gedächtnis und kein Abhängigkeitsgraph
  ---------------------------------------------------------------

  Ein Graph mit topologischer Sortierung wäre der Lehrbuchweg. Er lohnt sich
  ab Zehntausenden von Zellen, weil er gezielt nur das Betroffene neu
  rechnen kann. Hier wird ohnehin immer das ganze Blatt gerechnet — bei ein
  paar tausend Zellen sind das Mikrosekunden.

  Die Tiefensuche mit Gedächtnis liefert die richtige Reihenfolge von selbst:
  Wer einen Wert braucht, holt ihn, und wer schon gerechnet wurde, wird nicht
  zweimal gerechnet.

  ---------------------------------------------------------------
  Der Wächter, der von Anfang an drin sein muss
  ---------------------------------------------------------------

  `A1` rechnet `=B1`, `B1` rechnet `=A1`. Ohne `inArbeit` steigt die
  Tiefensuche unendlich ab und der Browser friert ein — nicht langsam,
  sondern sofort und ohne Fehlermeldung. Das ist kein Feinschliff für
  später, sondern Bedingung der ersten Fassung.
*/

/*
  Eine Obergrenze für aufgefaltete Bereiche. `SUMME(A1:Z1000)` auf einem
  Blatt mit acht Spalten soll nicht Zehntausende leere Zellen erzeugen —
  begrenzt wird ohnehin auf das, was es gibt, aber ein Tippfehler mit sechs
  Nullen darf den Reiter nicht anhalten.
*/
const HOECHSTENS_IM_BEREICH = 20000;

export function rechneDurch(gitter) {
  const spalten = gitter && gitter.spalten ? gitter.spalten : [];
  const zeilen = gitter && gitter.zeilen ? gitter.zeilen : [];
  const zellen = gitter && gitter.zellen ? gitter.zellen : new Map();

  const werte = new Map();
  const ringe = new Set();
  const inArbeit = new Set();

  function schluesselAn(spalte, zeile) {
    if (spalte < 0 || zeile < 0) return null;
    if (spalte >= spalten.length || zeile >= zeilen.length) return null;
    return zellschluessel(spalten[spalte].id, zeilen[zeile].id);
  }

  function werteVon(spalte, zeile) {
    const schluessel = schluesselAn(spalte, zeile);
    /*
      Außerhalb des Blattes ist LEER, nicht #BEZUG!. Ein Bereich darf über
      das Blattende hinausreichen — `SUMME(B2:B100)` soll auch dann
      arbeiten, wenn erst vierzig Zeilen da sind. Ein echter #BEZUG! kommt
      vom Verschieber und steht dann im Text der Formel.
    */
    if (!schluessel) return LEER;
    if (werte.has(schluessel)) return werte.get(schluessel);

    if (inArbeit.has(schluessel)) {
      ringe.add(schluessel);
      return fehler(FEHLER.ZIRKEL);
    }

    const roh = zellen.get(schluessel);
    if (roh === undefined || roh === null || roh === "") {
      werte.set(schluessel, LEER);
      return LEER;
    }

    if (!istFormel(roh)) {
      const gedeutet = deuteEingabe(roh, spalten[spalte].format || "zahl");
      const wert =
        gedeutet.art === "zahl"
          ? { art: "zahl", wert: gedeutet.wert }
          : gedeutet.art === "leer"
            ? LEER
            : { art: "text", wert: gedeutet.wert };
      werte.set(schluessel, wert);
      return wert;
    }

    inArbeit.add(schluessel);
    let ergebnis;
    try {
      ergebnis = werteAus(bauBaum(zerlege(roh)), umgebung);
    } catch {
      /*
        Eine Formel darf das Programm nicht anhalten. Was hier ankommt, ist
        entweder zu tief verschachtelt oder ein Fehler, den niemand bedacht
        hat — beides ist für den Nutzer dasselbe: Diese Formel geht nicht auf.
      */
      ergebnis = fehler(FEHLER.FORMEL);
    }
    inArbeit.delete(schluessel);

    if (istFehler(ergebnis) && ergebnis.code === FEHLER.ZIRKEL) ringe.add(schluessel);

    werte.set(schluessel, ergebnis);
    return ergebnis;
  }

  const umgebung = {
    holeZelle: werteVon,

    holeBereich(von, bis) {
      const vonSpalte = Math.max(0, Math.min(von.spalte, bis.spalte));
      const bisSpalte = Math.min(spalten.length - 1, Math.max(von.spalte, bis.spalte));
      const vonZeile = Math.max(0, Math.min(von.zeile, bis.zeile));
      const bisZeile = Math.min(zeilen.length - 1, Math.max(von.zeile, bis.zeile));

      const liste = [];
      for (let s = vonSpalte; s <= bisSpalte; s += 1) {
        for (let z = vonZeile; z <= bisZeile; z += 1) {
          liste.push(werteVon(s, z));
          if (liste.length >= HOECHSTENS_IM_BEREICH) return liste;
        }
      }
      return liste;
    },
  };

  /* Jede vorhandene Zelle einmal anstoßen — die Reihenfolge ergibt sich selbst. */
  for (let s = 0; s < spalten.length; s += 1) {
    for (let z = 0; z < zeilen.length; z += 1) {
      werteVon(s, z);
    }
  }

  return { werte, ringe };
}
